import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Search, Pencil, Trash2, FileText } from "lucide-react";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import useBlogs from "hooks/useBlogs";
import { createBlog, deleteBlog, editBlog } from "services/blog.service";
import BlogForm from "./BlogForm";
import { Blog, BlogCreate } from "types/blog.type";

const Blogs = () => {
  const { data, isLoading } = useBlogs(50);
  const [showAdd, setShowAdd] = useState(false);
  const [record, setRecord] = useState<Blog | null>(null);
  const [showDelete, setShowDelete] = useState(false);

  const queryClient = useQueryClient();

  const { isLoading: adding, mutate } = useMutation(
    async (input: BlogCreate) => await createBlog(input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        toggleAdd();
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const { isLoading: deleting, mutate: deleteMutate } = useMutation(
    async () => {
      if (!record?._id) return;
      return await deleteBlog(record._id);
    },
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        toggleDelete();
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const { isLoading: editing, mutate: editMutate } = useMutation(
    async (input: BlogCreate) => await editBlog(record?._id!, input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        queryClient.invalidateQueries();
        toggleAdd();
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const toggleAdd = () => setShowAdd((v) => !v);
  const toggleDelete = () => setShowDelete((v) => !v);

  const handleSubmit = (data: BlogCreate) => {
    record ? editMutate(data) : mutate(data);
  };

  const handleDelete = () => deleteMutate();

  const onEdit = (blog: Blog) => {
    setRecord(blog);
    setShowAdd(true);
  };

  const handleAdd = () => {
    setRecord(null);
    setShowAdd(true);
  };

  return (
    <div>
      <BreadCrumb title="Blog Posts" pageTitle="Blogs" />
      <ToastContainer closeButton={false} limit={1} />

      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-sm text-gray-500">
            {isLoading ? "Loading…" : `${data?.length ?? 0} posts`}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          <Plus size={16} />
          Create Post
        </button>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Loading posts…</div>
        ) : (data?.length ?? 0) === 0 ? (
          <div className="py-16 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FileText size={24} className="text-gray-400" />
            </div>
            <p className="text-gray-500 text-sm font-medium">No blog posts yet</p>
            <p className="text-gray-400 text-xs mt-1">Click "Create Post" to publish your first article.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-stack className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Read Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Published
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.map((blog) => (
                  <tr key={blog._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Title" data-cell="title" className="px-4 py-3 text-gray-900 font-medium sm:max-w-xs truncate">
                      {blog.title}
                    </td>
                    <td data-label="Category" className="px-4 py-3">
                      {blog.category ? (
                        <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
                          {blog.category}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td data-label="Read Time" className="px-4 py-3 text-gray-500">
                      {blog.readTime ?? "—"}
                    </td>
                    <td data-label="Published" className="px-4 py-3 text-gray-500">
                      {blog.createdAt
                        ? new Date(blog.createdAt).toLocaleDateString("en-NG", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "—"}
                    </td>
                    <td data-label="Actions" data-cell="actions" className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onEdit(blog)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                        >
                          <Pencil size={13} />
                          Edit
                        </button>
                        <button
                          onClick={() => { setRecord(blog); toggleDelete(); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                        >
                          <Trash2 size={13} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      <Modal
        show={showAdd}
        onHide={toggleAdd}
        id="blogModal"
        modal-center="true"
        size="xl"
      >
        <Modal.Header
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-base font-semibold text-gray-900">
            {record ? "Edit Post" : "Create Post"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-5 py-4 overflow-y-auto">
          <BlogForm
            onCancel={toggleAdd}
            onSubmit={handleSubmit}
            isLoading={adding || editing}
            data={record}
          />
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={showDelete}
        onHide={toggleDelete}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default Blogs;
